/**
 * The one product identity the whole export path keys on.
 *
 * Product rows are deduped by UPC and Position/Performance rows reference a
 * product by UPC, so UPC — not the id — is what the PSA format joins on. Any
 * step that grouped by id instead would split one product into several groups
 * whenever real data carries id variants for a single UPC ("050481" and
 * "50481" for the same item). The Performance aggregate is built per group, so
 * that split reports one position per variant, and the final dedupe by UPC
 * then keeps a single aggregate — a product on two shelves exporting as
 * numberOfPositions=1.
 *
 * The id is the fallback, used only where a product carries no usable UPC. It
 * is namespaced so an id can never collide with a bare UPC.
 */
type ProductIdentityInput = {
  upc?: string | number | null;
  productCode?: string | number | null;
};

const trimmed = (value: string | number | null | undefined) => String(value ?? "").trim();

const isUsable = (value: string) => value !== "" && value !== "0";

/** Accepts either half separately, for callers holding parsed PSA record fields. */
export const getProductIdentityFromParts = (
  upc: string | number | null | undefined,
  productId: string | number | null | undefined,
) => {
  const trimmedUpc = trimmed(upc);
  if (isUsable(trimmedUpc)) {
    return trimmedUpc;
  }

  return `no-upc::${trimmed(productId)}`;
};

export const getProductIdentity = (item: ProductIdentityInput) =>
  getProductIdentityFromParts(item.upc, item.productCode);
